import { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { get, includes, isFunction, isNil, isNull, isNaN, slice } from 'lodash';
import moment from 'moment';
import Icon from "../../Display/Icon/Icon";
import { momentizeValue, convertToUnixTimestamp, validateDate, shouldDisableDay } from "../../utils/Dates";
import { getPropagationPath } from "../../../utils/Browser";
import { fieldProps, getPreferredProp } from "../utils";
import Calendar from "../Calendar/Calendar";
import { containerClass, labelClass, dropDownClass, inputClass } from "./classNames";
import { isKeyPressed } from "../../../utils/isKeyPressed";
import { SPACEBAR_KEY_CODE, TAB_KEY_CODE } from "../../../utils/Browser/keyCodes";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Dropdown = _ref => {
  let {
    state,
    getMonth,
    id,
    isUtc,
    maxDate,
    minDate,
    onDaySelect,
    onEditYear,
    onNextMonth,
    onPreviousMonth,
    shouldDisableDate,
    calendarRef
  } = _ref;
  const getYearInput = () => {
    if (isNaN(state.year)) {
      return moment().year();
    } else if (isNull(state.yearValue)) {
      return state.year;
    }
    return state.yearValue;
  };
  return /*#__PURE__*/_jsxs("div", {
    id: `${id}-calendar`,
    className: dropDownClass({
      state
    }),
    ref: calendarRef,
    tabIndex: -1,
    role: "dialog",
    "aria-labelledby": `${id}-calendar-label`,
    "aria-describedby": `${id}-calendar-description`,
    children: [/*#__PURE__*/_jsxs("div", {
      className: "flex flex-row items-center justify-between select-none pb-1",
      children: [/*#__PURE__*/_jsx(Icon, {
        icon: "ChevronArrow",
        onClick: onPreviousMonth,
        ariaLabel: `Previous Month: ${getMonth(state).subtract(1, 'month').format('MMMM YYYY')}`,
        role: "button",
        tabIndex: 0,
        className: "flex-none w-6 text-center",
        direction: "left",
        color: "action-blue",
        size: 15
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex-1 text-sm text-center text-text-blue font-heavy-font",
        children: [/*#__PURE__*/_jsxs("span", {
          children: [getMonth(state).format('MMMM'), "\xA0"]
        }), /*#__PURE__*/_jsx("label", {
          htmlFor: `${id}-year-input`,
          className: "sr-only",
          children: "Year"
        }), /*#__PURE__*/_jsx("input", {
          id: `${id}-year-input`,
          value: getYearInput(),
          onChange: onEditYear,
          className: "border-none text-sm w-10 inline-block p-0"
        })]
      }), /*#__PURE__*/_jsx(Icon, {
        icon: "ChevronArrow",
        onClick: onNextMonth,
        ariaLabel: `Next Month: ${getMonth(state).add(1, 'month').format('MMMM YYYY')}`,
        role: "button",
        tabIndex: 0,
        className: "flex-none w-6 text-center",
        direction: "right",
        color: "action-blue",
        size: 15
      })]
    }), /*#__PURE__*/_jsx(Calendar, {
      year: getYearInput(),
      month: isNaN(state.month) ? moment().month() : state.month,
      onDayClick: onDaySelect,
      selectedDays: [state.value],
      isUtc: isUtc,
      shouldDisableDay: day => shouldDisableDay(day, maxDate, minDate, shouldDisableDate),
      className: "flex justify-around",
      role: "grid",
      "aria-label": "Calendar grid"
    })]
  });
};
Dropdown.propTypes = {
  getMonth: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  isUtc: PropTypes.bool.isRequired,
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  onDaySelect: PropTypes.func.isRequired,
  onEditYear: PropTypes.func.isRequired,
  onNextMonth: PropTypes.func.isRequired,
  onPreviousMonth: PropTypes.func.isRequired,
  shouldDisableDate: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  calendarRef: PropTypes.object
};
const DateField = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    className = '',
    disabled = false,
    hideLabel = false,
    hint = '',
    inline = false,
    labelClassName = '',
    name = '',
    placeholder = '',
    required = false,
    style = {
      container: {},
      label: {},
      input: {},
      hint: {},
      error: {}
    },
    dateFormat = 'MM/DD/YYYY',
    valueFormat = convertToUnixTimestamp,
    isUtc = true,
    maxDate = null,
    minDate = null,
    customErrorMessage = '',
    shouldDisableDate = () => false,
    blurOnEnterKeyDown = false,
    onBlur = null,
    id,
    label
  } = props;
  const [state, setState] = useState(() => {
    const value = momentizeValue(getPreferredProp(props, 'value'), dateFormat, isUtc);
    return {
      isOpen: false,
      value,
      inputValue: null,
      yearValue: null,
      year: value ? value.year() : moment().year(),
      month: value ? value.month() : moment().month(),
      hasError: false,
      errorMessage: '',
      showRequiredError: false
    };
  });
  const elementRef = useRef(ref);
  const calendarRef = useRef(null);
  const hasFocusRef = useRef(false);
  const onChange = get(props, 'onChange', getPreferredProp(props, 'onChange'));
  const onFocus = get(props, 'onFocus', getPreferredProp(props, 'onFocus'));
  const handleClickOutside = useCallback(e => {
    if (!includes(getPropagationPath(e), elementRef.current)) {
      setState(prevState => ({
        ...prevState,
        isOpen: false
      }));
      triggerOnBlur(false);
    }
  }, [onBlur]);
  useEffect(() => {
    if (state.isOpen) {
      document.addEventListener('click', handleClickOutside, true);
      if (calendarRef.current) {
        calendarRef.current.focus();
      }
    } else {
      document.removeEventListener('click', handleClickOutside, true);
    }
  }, [state.isOpen, handleClickOutside]);
  useEffect(() => {
    if (blurOnEnterKeyDown) {
      const handleKeyDown = event => {
        if (event.key === 'Enter') {
          handleBlur(event);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [blurOnEnterKeyDown]);
  const updateHasError = value => {
    const date = momentizeValue(value, dateFormat, isUtc);
    const validationMessage = validateDate(date, maxDate, minDate, shouldDisableDate, dateFormat, customErrorMessage);
    setState(prevState => ({
      ...prevState,
      hasError: !!validationMessage,
      errorMessage: validationMessage || ''
    }));
  };
  const handleOnChange = date => {
    if (!date) {
      return onChange('');
    }
    const value = isUtc ? moment.utc(moment(date).format(dateFormat), dateFormat) : moment(date);
    if (isFunction(valueFormat)) {
      return onChange(valueFormat(value));
    }
    return onChange(value.format(valueFormat));
  };
  const handleInputChange = e => {
    let value = get(e, 'target.value', '');
    setState(prevState => ({
      ...prevState,
      inputValue: value,
      isOpen: false
    }));
    updateHasError(value);
  };
  const triggerOnBlur = isOpen => {
    if (!isOpen && required) {
      setState(prevState => {
        if (!prevState.value) {
          return {
            ...prevState,
            showRequiredError: true
          };
        }
        return {
          ...prevState
        };
      });
    } else {
      setState(prevState => ({
        ...prevState,
        showRequiredError: false
      }));
    }
    if (isFunction(getPreferredProp(props, 'onBlur'))) {
      getPreferredProp(props, 'onBlur')();
    }
  };
  const handleFocus = () => {
    hasFocusRef.current = true;
    onFocus && onFocus();
  };
  const handleClick = () => {
    setState(prevState => ({
      ...prevState,
      isOpen: !prevState.isOpen
    }));
  };
  const handleBlur = e => {
    if (e.target.value && e.target.value.length < 3) {
      setState(prevState => ({
        ...prevState,
        inputValue: null,
        value: null
      }));
      updateHasError(null);
      handleOnChange('');
      if (onBlur) onBlur();
      return;
    }
    if (!isNull(state.inputValue)) {
      const value = momentizeValue(e.target.value, dateFormat, isUtc);
      setState({
        inputValue: null,
        value,
        year: value ? value.year() : moment().year(),
        month: value ? value.month() : moment().month(),
        yearValue: null
      });
      handleOnChange(value);
      updateHasError(value);
    }
    triggerOnBlur(state.isOpen);
  };
  const handleInputKeyDown = e => {
    if (isKeyPressed(e, ' ', SPACEBAR_KEY_CODE)) {
      e.preventDefault();
      setState(prevState => ({
        ...prevState,
        isOpen: !prevState.isOpen
      }));
    } else if (isKeyPressed(e, 'Tab', TAB_KEY_CODE)) {
      setState(prevState => ({
        ...prevState,
        isOpen: false
      }));
    }
  };
  const handleDaySelect = day => {
    const value = isUtc ? day.utc() : day;
    setState(prevState => ({
      ...prevState,
      isOpen: false,
      value,
      year: value ? value.year() : moment().year(),
      month: value ? value.month() : moment().month()
    }));
    handleOnChange(day);
    updateHasError(value);
    triggerOnBlur(false);
  };
  const getMonth = state => {
    // if invalid date, return current month to reset calendar
    if (isNaN(state.month)) {
      return moment({
        month: moment().month(),
        year: moment().year()
      });
    }
    return moment({
      month: state.month,
      year: state.year
    });
  };
  const handlePreviousMonth = () => {
    const prev = getMonth(state).subtract(1, 'month');
    setState(prevState => ({
      ...prevState,
      month: prev.month(),
      year: prev.year(),
      yearValue: null
    }));
  };
  const handleNextMonth = () => {
    const next = getMonth(state).add(1, 'month');
    setState(prevState => ({
      ...prevState,
      month: next.month(),
      year: next.year(),
      yearValue: null
    }));
  };
  const handleEditYear = e => {
    const value = slice(e.target.value, 0, 4).toString().replace(/\D/g, '');
    setState(prevState => ({
      ...prevState,
      yearValue: value
    }));
    if (value && value.length === 4) {
      setState(prevState => ({
        ...prevState,
        year: parseInt(value, 10),
        yearValue: null
      }));
    }
  };
  const getInputValue = () => {
    if (isNil(state.inputValue)) {
      if (state.value) {
        return state.value.format(dateFormat);
      }
      return '';
    }
    return state.inputValue;
  };
  const ariaLabel = typeof label === 'string' ? label.toLowerCase() : '';
  return /*#__PURE__*/_jsxs("div", {
    className: containerClass({
      inline,
      hasError: state.hasError,
      disabled,
      className,
      state
    }),
    style: style.container,
    ref: elementRef,
    children: [/*#__PURE__*/_jsx("label", {
      htmlFor: id,
      className: labelClass({
        inline,
        hideLabel,
        state,
        labelClassName,
        required
      }),
      style: style.label,
      children: label
    }), /*#__PURE__*/_jsxs("div", {
      className: "relative group",
      children: [/*#__PURE__*/_jsx("input", {
        id: id,
        value: getInputValue(),
        onChange: handleInputChange,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onClick: handleClick,
        onKeyDown: handleInputKeyDown // Handle space key
        ,
        placeholder: placeholder || dateFormat,
        name: getPreferredProp(props, 'name'),
        disabled: disabled,
        type: "text",
        style: style.input,
        className: inputClass({
          state
        }),
        "aria-expanded": state.isOpen // Indicate if the calendar is open
        ,
        "aria-haspopup": "dialog",
        "aria-controls": `${id}-calendar` // Relate to the calendar
        ,
        "aria-describedby": `${id}-instructions` // Relate to the instructions
      }), /*#__PURE__*/_jsx("span", {
        id: `${id}-instructions`,
        className: "sr-only",
        children: "Press space to open or close the calendar. Use arrow keys to navigate and press Enter to select a date."
      }), /*#__PURE__*/_jsx("div", {
        className: "absolute top-0 right-0 p-2.5",
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "Calendar",
          disabled: disabled || state.value && !state.value.isValid(),
          color: "medium-grey",
          ariaLabel: `expand calendar to select ${state.hasError ? `${ariaLabel} ${state.errorMessage}` : ariaLabel}`,
          onClick: handleClick
        })
      }), state.isOpen && /*#__PURE__*/_jsx(Dropdown, {
        id: `${id}-calendar`,
        state: state,
        isUtc: isUtc,
        maxDate: maxDate,
        minDate: minDate,
        onDaySelect: handleDaySelect,
        onEditYear: handleEditYear,
        onNextMonth: handleNextMonth,
        onPreviousMonth: handlePreviousMonth,
        shouldDisableDate: shouldDisableDate,
        getMonth: getMonth,
        calendarRef: calendarRef
      }), !state.isOpen && hint && /*#__PURE__*/_jsx("div", {
        className: "text-xs font-regular-font text-dark-grey leading-4 mt-1",
        style: style.hint,
        children: /*#__PURE__*/_jsx("span", {
          className: "italic",
          children: hint
        })
      }), !state.isOpen && /*#__PURE__*/_jsxs("div", {
        "aria-live": "polite",
        role: "alert",
        children: [state.hasError && /*#__PURE__*/_jsx("p", {
          className: "text-red text-xs",
          children: state.errorMessage
        }), !state.hasError && state.showRequiredError && /*#__PURE__*/_jsx("p", {
          className: "text-red text-xs",
          children: "Required"
        })]
      })]
    })]
  });
});
DateField.propTypes = {
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** className for reference */
  className: PropTypes.string,
  /** Override the inline-styles of the elements || **Not Recommended** */
  style: PropTypes.shape({
    container: PropTypes.object,
    label: PropTypes.object,
    input: PropTypes.object,
    hint: PropTypes.object,
    error: PropTypes.object
  }),
  /** label text for button */
  label: PropTypes.node.isRequired,
  /** Should the label be displayed inline with the input? */
  inline: PropTypes.bool,
  /** Is the input disabled? */
  disabled: PropTypes.bool,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Should the label be hidden? */
  hideLabel: PropTypes.bool,
  /** Help text displayed below the input */
  hint: PropTypes.node,
  /** Is the field required */
  required: PropTypes.bool,
  /** How the date should be formatted in the input */
  dateFormat: PropTypes.string,
  /**
   * Specify the value format you want
   * @param date:moment
   */
  valueFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** Should the component work with UTC moment? */
  isUtc: PropTypes.bool,
  /** Max permitted date */
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  /** Min permitted date */
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  /**
   * Should this date be disabled?
   * @param date:moment
   */
  shouldDisableDate: PropTypes.func,
  /** Should the input blur when pressing enter */
  blurOnEnterKeyDown: PropTypes.bool,
  /** See fieldProps doc */
  ...fieldProps
};
DateField.displayName = 'DateField';
export default DateField;
//# sourceMappingURL=DateField.js.map